import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, Building2, Briefcase, CreditCard, ScrollText, Menu, PanelLeftClose, PanelLeft, LogOut, KeyRound, Loader2, Mail, ShieldCheck, Globe } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Toaster } from "../components/ui/sonner";
import { useAdminAuth } from "./AdminAuthContext";

const NAV_ITEMS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true, testid: "admin-nav-dashboard" },
  { to: "/admin/users", label: "Usuarios", icon: Users, testid: "admin-nav-users" },
  { to: "/admin/companies", label: "Empresas", icon: Building2, testid: "admin-nav-companies" },
  { to: "/admin/jobs", label: "Empleos", icon: Briefcase, testid: "admin-nav-jobs" },
  { to: "/admin/trust-safety", label: "Trust & Safety", icon: ShieldCheck, testid: "admin-nav-trust-safety" },
  { to: "/admin/plans", label: "Planes y Accesos", icon: CreditCard, testid: "admin-nav-plans" },
  { to: "/admin/audit", label: "Auditoría", icon: ScrollText, testid: "admin-nav-audit" },
  { to: "/admin/external-jobs", label: "Fuentes Externas", icon: Globe, testid: "admin-nav-external-jobs" },
  // Temporary diagnostic tool — remove once the production Resend issue is closed.
  { to: "/admin/diagnostics/email", label: "Diagnóstico Email", icon: Mail, testid: "admin-nav-email-diagnostics", superAdminOnly: true },
];

function NavLinks({ collapsed, onNavigate, role }) {
  return (
    <nav className="flex-1 py-3 space-y-1 px-2">
      {NAV_ITEMS.filter((item) => !item.superAdminOnly || role === "super_admin").map(({ to, label, icon: Icon, exact, testid }) => (
        <NavLink
          key={to}
          to={to}
          end={exact}
          data-testid={testid}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              isActive ? "bg-[#028CF8] text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`
          }
        >
          <Icon className="h-4.5 w-4.5 shrink-0" />
          {!collapsed && <span className="truncate">{label}</span>}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AdminLayout() {
  const { user, loading, adminLogout } = useAdminAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/admin/login", { replace: true }); return; }
    if (user.must_change_password) { navigate("/admin/change-password", { replace: true }); return; }
  }, [user, loading, navigate]);

  if (loading || !user || user.must_change_password) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <Loader2 className="h-6 w-6 text-white animate-spin" />
      </div>
    );
  }

  const handleLogout = () => {
    setDrawerOpen(false);
    navigate("/admin/login", { replace: true });
    requestAnimationFrame(() => requestAnimationFrame(() => adminLogout()));
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="admin-layout">
      <aside className={`hidden md:flex md:flex-col bg-slate-900 shrink-0 transition-all duration-200 ${collapsed ? "md:w-16" : "md:w-60"}`}>
        <div className="h-16 flex items-center justify-center border-b border-slate-800 px-2">
          {collapsed ? <span className="text-white font-bold">ICJ</span> : <span className="text-white font-bold tracking-tight">InfoCityJobs Admin</span>}
        </div>
        <NavLinks collapsed={collapsed} role={user.role} />
        <div className="p-2 border-t border-slate-800">
          <button
            data-testid="admin-logout-button"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-300 hover:bg-slate-800 hover:text-white"
          >
            <LogOut className="h-4.5 w-4.5 shrink-0" />
            {!collapsed && <span>Cerrar sesión</span>}
          </button>
        </div>
      </aside>

      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" data-testid="admin-mobile-drawer" className="w-64 bg-slate-900 border-none p-0 flex flex-col">
          <SheetHeader className="h-16 flex items-center justify-center border-b border-slate-800 px-2">
            <SheetTitle className="text-white font-bold tracking-tight">InfoCityJobs Admin</SheetTitle>
          </SheetHeader>
          <NavLinks collapsed={false} onNavigate={() => setDrawerOpen(false)} role={user.role} />
          <div className="p-2 border-t border-slate-800">
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-300 hover:bg-slate-800 hover:text-white">
              <LogOut className="h-4.5 w-4.5 shrink-0" /> <span>Cerrar sesión</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-6 shrink-0">
          <div className="flex items-center gap-2">
            <button data-testid="admin-mobile-menu-button" onClick={() => setDrawerOpen(true)} className="md:hidden p-2 rounded-lg hover:bg-slate-100">
              <Menu className="h-5 w-5 text-slate-700" />
            </button>
            <button data-testid="admin-sidebar-collapse-toggle" onClick={() => setCollapsed((c) => !c)} className="hidden md:flex p-2 rounded-lg hover:bg-slate-100">
              {collapsed ? <PanelLeft className="h-5 w-5 text-slate-700" /> : <PanelLeftClose className="h-5 w-5 text-slate-700" />}
            </button>
          </div>
          <div className="flex items-center gap-3 md:gap-4">
            <span data-testid="admin-topbar-email" className="text-xs md:text-sm text-slate-500 truncate max-w-[140px] md:max-w-none">{user.email}</span>
            <button data-testid="admin-topbar-change-password" onClick={() => navigate("/admin/change-password")} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600" aria-label="Cambiar contraseña">
              <KeyRound className="h-4.5 w-4.5" />
            </button>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-6 overflow-x-hidden" data-testid="admin-main-content">
          <Outlet />
        </main>
      </div>
      <Toaster />
    </div>
  );
}
